import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { client } from "../../utils/axios";
import { log } from "../logger";
import "./UserPrescription.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const bgImage = require("../../images/splash.19dd94a414793b5c0423.jpg");

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 30;

const UserPrescription = () => {
  const location = useLocation();
  const searchParam = new URLSearchParams(location.search);
  const visitId = searchParam.get("visitId");
  log("visitIdValue:", visitId);

  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  const timerRef = useRef(null);
  const firstOtpInputRef = useRef(null);

  // ─── Timer logic ────────────────────────────────────────────────────────────

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    setResendTimer(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  // Auto-focus first OTP input when step changes to otp
  useEffect(() => {
    if (step === "otp") {
      setTimeout(() => firstOtpInputRef.current?.focus(), 100);
    }
  }, [step]);

  // ─── OTP Send ───────────────────────────────────────────────────────────────

  const sendOtp = useCallback(
    (isResend = false) => {
      if (!mobile || mobile.length < 10) {
        setError("Enter valid mobile number");
        return;
      }

      isResend ? setResendLoading(true) : setLoading(true);
      setError("");

      client
        .post("/visit/send-otp", { mobile, visitId })
        .then((resp) => {
          if (!resp || resp.status !== 200) {
            setError(resp?.data?.message || "Error sending OTP");
            return;
          }
          startTimer();
          if (!isResend) setStep("otp");
        })
        .catch((err) => {
          if (err?.response?.status === 429) {
            setError("Too many attempts. Please wait 1 minute before retrying.");
            return;
          }
          setError(err?.response?.data?.message || "Error sending OTP");
        })
        .finally(() => {
          isResend ? setResendLoading(false) : setLoading(false);
        });
    },
    [mobile, visitId, startTimer]
  );

  const handleResend = useCallback(() => {
    if (resendTimer > 0 || resendLoading) return;
    setOtpDigits(Array(OTP_LENGTH).fill(""));
    setError("");
    sendOtp(true);
  }, [resendTimer, resendLoading, sendOtp]);

  // ─── Go Back ────────────────────────────────────────────────────────────────

  const goBack = useCallback(() => {
    clearTimer();
    setResendTimer(0);
    setOtpDigits(Array(OTP_LENGTH).fill(""));
    setError("");
    setStep("mobile");
  }, [clearTimer]);

  // ─── Mobile change ───────────────────────────────────────────────────────────

  const handleMobileChange = useCallback(
    (e) => {
      const val = e.target.value;
      setMobile(val);
      if (error) setError("");
    },
    [error]
  );

  // ─── OTP Input handlers ─────────────────────────────────────────────────────

  const handleOtpChange = useCallback(
    (value, index) => {
      if (!/^\d?$/.test(value)) return;
      const updated = [...otpDigits];
      updated[index] = value;
      setOtpDigits(updated);
      if (error) setError("");
      if (value && index < OTP_LENGTH - 1) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    },
    [otpDigits, error]
  );

  const handleOtpKeyDown = useCallback(
    (e, index) => {
      if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
        document.getElementById(`otp-${index - 1}`)?.focus();
      }
    },
    [otpDigits]
  );

  // Handle paste — fill all 6 digits at once
  const handleOtpPaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const updated = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((char, i) => (updated[i] = char));
    setOtpDigits(updated);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    document.getElementById(`otp-${focusIndex}`)?.focus();
  }, []);

  // ─── Verify OTP ─────────────────────────────────────────────────────────────

  const verifyOtp = async () => {
    const otp = otpDigits.join("");
    if (otp.length !== OTP_LENGTH) {
      setError("Please enter valid 6-digit OTP");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const verifyRes = await client.post("/visit/verify-otp", { mobile, otp });

      if (!verifyRes || verifyRes.status !== 200) {
        setError(verifyRes?.data?.message || "Invalid OTP");
        return;
      }

      const token = verifyRes.data?.token;
      if (!token) {
        setError("Something went wrong. Please try again.");
        return;
      }

      localStorage.setItem("prescription_token", token);

      const res = await client.post(
        "/visit/print-prescription-report",
        { visitId },
        {
          responseType: "text",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res || !res.data) {
        setError("No prescription found");
        return;
      }

      // ── Store raw HTML — iframe handles full HTML doc natively ──────────────
      clearTimer();
      setHtml(res.data);
      setStep("done");

    } catch (err) {
      if (err?.response?.status === 400) {
        setError(err?.response?.data?.message || "Invalid OTP");
        setOtpDigits(Array(OTP_LENGTH).fill(""));
        setTimeout(() => firstOtpInputRef.current?.focus(), 50);
        return;
      }
      if (err?.response?.status === 401) {
        setError("Session expired. Please try again.");
        setStep("mobile");
        return;
      }
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── PDF Download ───────────────────────────────────────────────────────────

const handleDownload = () => {
  setPdfLoading(true);

  const iframe = document.getElementById("prescription-frame");
  const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;

  if (!iframeDoc) {
    console.error("iframe not found");
    setPdfLoading(false);
    return;
  }

  // ── Step 1: Convert all images in iframe to base64 first ───────────────────
  const imgElements = iframeDoc.querySelectorAll("img");

  const convertImages = Array.from(imgElements).map((img) => {
    return new Promise((resolve) => {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("data:")) return resolve();

      fetch(src, { mode: "cors" })
        .then((res) => res.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            img.src = reader.result;
            resolve();
          };
          reader.onerror = () => resolve();
          reader.readAsDataURL(blob);
        })
        .catch(() => {
          img.style.display = "none"; // hide broken image silently
          resolve();
        });
    });
  });

  Promise.all(convertImages)
    .then(() => {
      // ── Step 2: Render full iframe body with correct styles ───────────────
      return html2canvas(iframeDoc.body, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
        width: iframeDoc.body.scrollWidth,
        height: iframeDoc.body.scrollHeight,
        windowWidth: iframeDoc.body.scrollWidth,
        windowHeight: iframeDoc.body.scrollHeight,
      });
    })
    .then((canvas) => {
      const imgData = canvas.toDataURL("image/jpeg", 0.98);

      // ── Step 3: Calculate pages — handle long prescriptions ───────────────
      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();   // 210mm
      const pdfHeight = pdf.internal.pageSize.getHeight(); // 297mm
      const imgHeightInMm = (canvas.height * pdfWidth) / canvas.width;

      if (imgHeightInMm <= pdfHeight) {
        // ── Single page ───────────────────────────────────────────────────
        pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, imgHeightInMm);
      } else {
        // ── Multi page — slice canvas into A4 pages ───────────────────────
        const pageHeightInPx = (pdfHeight * canvas.width) / pdfWidth;
        let yOffset = 0;
        let pageNum = 0;

        while (yOffset < canvas.height) {
          const sliceHeight = Math.min(pageHeightInPx, canvas.height - yOffset);

          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          pageCanvas.height = sliceHeight;

          const ctx = pageCanvas.getContext("2d");
          ctx.drawImage(
            canvas,
            0, yOffset,           // source x, y
            canvas.width, sliceHeight, // source width, height
            0, 0,                 // dest x, y
            canvas.width, sliceHeight  // dest width, height
          );

          const pageImg = pageCanvas.toDataURL("image/jpeg", 0.98);
          const pageImgHeight = (sliceHeight * pdfWidth) / canvas.width;

          if (pageNum > 0) pdf.addPage();
          pdf.addImage(pageImg, "JPEG", 0, 0, pdfWidth, pageImgHeight);

          yOffset += sliceHeight;
          pageNum++;
        }
      }

      pdf.save("prescription.pdf");
    })
    .catch((err) => console.error("PDF generation failed:", err))
    .finally(() => setPdfLoading(false));
};
  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div
        className="up-container"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="up-loading-text">Loading...</div>
      </div>
    );
  }

  // ── Done step — iframe isolates backend HTML from React DOM ─────────────────
  if (step === "done") {
    if (!html) return <div>No prescription found</div>;
    return (
      <div style={{ position: "relative" }}>

        {/* Button lives outside iframe — always safe, never overridden */}
        <button
          onClick={handleDownload}
          disabled={pdfLoading}
          style={{
            position: "fixed",
            top: "16px",
            right: "16px",
            zIndex: 99999,
            padding: "10px 24px",
            background: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            fontSize: "15px",
            fontWeight: "700",
            cursor: pdfLoading ? "not-allowed" : "pointer",
            opacity: pdfLoading ? 0.7 : 1,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          }}
        >
          {pdfLoading ? "Generating PDF..." : "Download PDF"}
        </button>

        {/* Prescription HTML fully isolated in iframe */}
        <iframe
          id="prescription-frame"
          srcDoc={html}
          title="Prescription"
          style={{ width: "100%", border: "none", display: "block" }}
          onLoad={(e) => {
            // Auto-resize to full content height — no scrollbar
            const iframe = e.target;
            const contentHeight =
              iframe.contentDocument?.body?.scrollHeight || 800;
            iframe.style.height = contentHeight + 50 + "px";
          }}
        />

      </div>
    );
  }

  return (
    <div
      className="up-container"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="up-card">

        {step === "otp" && (
          <button className="up-back-btn" onClick={goBack}>
            &#8592;
          </button>
        )}

        {step === "mobile" && (
          <>
            <label className="up-label">Enter Your Mobile Number</label>
            <input
              className="up-input"
              value={mobile}
              onChange={handleMobileChange}
              placeholder="Enter Your Phone Number"
              type="tel"
              maxLength={10}
            />
            <p className="up-hint">We will send an OTP to this number</p>
            <button
              className="up-btn"
              onClick={() => sendOtp(false)}
              disabled={loading}
            >
              {loading ? "Sending..." : "Login"}
            </button>
            {error && <div className="up-error">{error}</div>}
          </>
        )}

        {step === "otp" && (
          <>
            <h2 className="up-title up-title--otp">OTP Verification</h2>
            <p className="up-subtitle">
              Enter the 6-digit OTP sent to <strong>{mobile}</strong>
            </p>

            <div className="up-otp-row">
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  id={`otp-${i}`}
                  ref={i === 0 ? firstOtpInputRef : null}
                  className="up-otp-box"
                  value={digit}
                  maxLength={1}
                  onChange={(e) => handleOtpChange(e.target.value, i)}
                  onKeyDown={(e) => handleOtpKeyDown(e, i)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  type="tel"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                />
              ))}
            </div>

            {/* Resend row */}
            <div className="up-resend">
              {resendTimer > 0 ? (
                <span className="up-resend-timer">
                  Resend OTP in <strong>{resendTimer}s</strong>
                </span>
              ) : (
                <button
                  className="up-resend-link"
                  onClick={handleResend}
                  disabled={resendLoading}
                >
                  {resendLoading ? "Sending..." : "Resend OTP"}
                </button>
              )}
            </div>

            <button
              className="up-btn"
              onClick={verifyOtp}
              disabled={loading}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>

            {error && <div className="up-error">{error}</div>}
          </>
        )}

      </div>
    </div>
  );
};

export default UserPrescription;